import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ROLES, ROLE_DESCRIPTION } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import { repository } from '../data'
import type { TeamMember } from '../data'

/**
 * Five checkboxes, one per role.
 *
 * A checkbox rather than a dropdown or a multi-select because the roles are
 * independent and few: five lines, each either ticked or not, is the shortest
 * distance between "what may this person do" and an answer. A multi-select
 * would hide four of the five behind a tap, and the value of this screen is
 * reading it, not filling it in.
 *
 * The tick is written immediately and the row says so. Two failures are worth
 * distinguishing and both land here: the *refusal* (§11.4's rules — the last
 * manager, or nobody at all) which is a sentence the person needs to read, and
 * the dropped request, which is not their problem to understand. Either way the
 * box goes back to what the server last agreed to, because a checkbox that
 * stays ticked after a failed write is a lie about the system.
 */
export function RoleChecks({ member }: { member: TeamMember }): JSX.Element {
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState('')

  const save = useMutation({
    mutationFn: (next: readonly Role[]) => repository.setRoles(member.id, next),
    onSuccess: () => {
      setProblem('')
      void queryClient.invalidateQueries({ queryKey: ['team'] })
      // Your own nav changes the moment your roles do — including when the
      // person you just edited is you.
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const toggle = (role: Role, on: boolean): void => {
    const next = on ? [...member.roles, role] : member.roles.filter((r) => r !== role)
    save.mutate(next)
  }

  return (
    <div className="role-toggles">
      {ROLES.map((role) => {
        const held = member.roles.includes(role)
        return (
          <button
            key={role}
            className="role-toggle"
            aria-pressed={held}
            title={ROLE_DESCRIPTION[role]}
            disabled={save.isPending}
            onClick={() => toggle(role, !held)}
          >
            {ROLE_LABEL[role]}
          </button>
        )
      })}
      {problem ? (
        <p className="critical role-toggles__problem" role="alert">
          {problem}
        </p>
      ) : null}
    </div>
  )
}

export const ROLE_LABEL: Readonly<Record<Role, string>> = {
  admin: 'Admin',
  engineer: 'Engineer',
  technician: 'Technician',
  intake: 'Intake',
}
